import React from "react";
import { useSelector } from "react-redux";

import { Header, Title, Breadcrumbs } from "../../../components";
import { HallsItem } from "../components";
import { HallsItemSkeleton } from "../components/HallsItemSkeleton";

import { useAppDispatch } from "../../../redux/store";
import { fetchGetHalls } from "../../../redux/halls/asyncActions";
import { hallsSelector } from "../../../redux/halls/selectors";
import { fetchGetHallsGallery } from "../../../redux/images/asyncActions";
import { imagesSelector } from "../../../redux/images/selectors";

import styles from "./Halls.module.scss";

import { hallImages } from "../../../resources/constants";
import { HallsPropsType } from "./types";
import { IHall } from "../../../redux/halls/types";
import { useLocation } from "react-router-dom";

export const Halls: React.FC<HallsPropsType> = ({ title }) => {
  const { halls, isLoading: hallsLoading } = useSelector(hallsSelector);
  const { hallsGallery, isLoading: imagesLoading } = useSelector(imagesSelector);
  const dispatch = useAppDispatch();

  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    dispatch(fetchGetHalls());
    dispatch(fetchGetHallsGallery());
  }, []);

  const isLoading = hallsLoading || imagesLoading;
  const hallsItems = renderHallsItems(hallImages, halls, hallsGallery);

  function renderHallsItems(
    hallsImages: { [key: number]: string },
    hallsData: IHall[],
    galleryData: Array<{ title: string; images: Array<{ url: string; img_url?: string }> }>
  ) {
    if (!hallsData || hallsData.length === 0) {
      return null;
    }

    const galleryMap = new Map<string, Array<{ url: string }>>();
    
    if (galleryData && galleryData.length > 0) {
      galleryData.forEach((galleryItem) => {
        if (galleryItem.images && galleryItem.images.length > 0) {
          const formattedImages = galleryItem.images.map((img) => ({
            url: img.url || img.img_url || ''
          })).filter((img) => img.url);
          
          if (formattedImages.length > 0) {
            galleryMap.set(galleryItem.title, formattedImages);
          }
        }
      });
    }

    const groupedHalls = hallsData.reduce((acc: { [key: string]: IHall }, hall: IHall) => {
      const title = hall.title;
      
      const galleryImages = galleryMap.get(title) || [];
      
      if (!acc[title]) {
        const allImages = [
          ...(hall.images || []),
          ...galleryImages
        ];
        
        const uniqueImages = allImages.reduce((unique: Array<{ url: string }>, img: { url: string }) => {
          if (img.url && !unique.find((u) => u.url === img.url)) {
            unique.push(img);
          }
          return unique;
        }, []);
        
        acc[title] = { ...hall, images: uniqueImages };
      } else {
        const existingUrls = new Set(acc[title].images.map((img: { url: string }) => img.url));
        const newImages = [
          ...(hall.images || []),
          ...galleryImages
        ].filter((img: { url: string }) => img.url && !existingUrls.has(img.url));
        acc[title].images = [...acc[title].images, ...newImages];
      }
      return acc;
    }, {});

    const groupedHallsArray = Object.values(groupedHalls);

    return groupedHallsArray.map((curHallData: IHall) => (
      <HallsItem key={curHallData.id} data={curHallData} images={curHallData.images} />
    ));
  }

  return (
    <>
      <Header title={title} />
      <main>
        <div className="container">
          <Breadcrumbs />
        </div>
        <section className="container container--mobileFull">
          <Title>{title}</Title>
          <div className={styles.items}>
            {isLoading ? (
              <>
                <HallsItemSkeleton />
                <HallsItemSkeleton />
              </>
            ) : (
              hallsItems
            )}
          </div>
        </section>
      </main>
    </>
  );
};
